import { useCallback, useRef, useState } from 'react';
import { Upload, X, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import wordIcon from '../../assets/word.svg';

interface ImportUploadStepProps {
  onFilesSelected: (files: File[]) => void;
  selectedFiles: File[];
  onRemoveFile: (index: number) => void;
  onNext: () => void;
  hasFiles: boolean;
  onBack: () => void;
}

export const ImportUploadStep = ({
  onFilesSelected,
  selectedFiles,
  onRemoveFile,
  onNext,
  hasFiles,
  onBack,
}: ImportUploadStepProps) => {
  const { t } = useTranslation('knowledge');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const duplicates: string[] = [];
      const accepted = Array.from(files).filter((f) => {
        // Only accept .doc or .docx files
        if (!/\.(docx?|doc)$/i.test(f.name)) return false;

        // Check for duplicates
        const isDuplicate = selectedFiles.some(
          (existing) => existing.name === f.name
        );
        if (isDuplicate) {
          duplicates.push(f.name);
          return false;
        }
        return true;
      });

      if (duplicates.length > 0) {
        toast.error(
          duplicates.length === 1
            ? t('importModal.duplicateFile', { fileName: duplicates[0] })
            : t('importModal.duplicateFiles', { count: duplicates.length })
        );
      }

      if (accepted.length > 0) {
        onFilesSelected(accepted);
      }
    },
    [onFilesSelected, selectedFiles]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) setIsDragging(true);
    },
    [isDragging]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFiles]
  );

  return (
    <div className="h-full flex flex-col">
      <input
        ref={fileInputRef}
        type="file"
        accept=".doc,.docx"
        multiple
        onChange={handleInputChange}
        className="hidden"
      />

      <div className="flex-1 overflow-y-auto mx-10 mb-6">
        <div className="mx-auto w-[50%] mt-6">
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-3',
              'px-10 py-12 rounded-lg border-2 border-dashed',
              'transition-colors cursor-pointer',
              isDragging ? 'bg-primary/5' : 'bg-gray-50 hover:bg-gray-100'
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-center">
              <span className="text-primary font-medium cursor-pointer">
                {t('importModal.browse')}
              </span>{' '}
              {t('importModal.orDragAndDrop')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('importModal.fileTypes')}
            </p>
          </div>
        </div>

        <div className="mt-10">
          <h3 className="text-base font-medium text-slate-700 mb-4">
            {t('importModal.uploadedFiles')}
          </h3>
          {selectedFiles.length > 0 ? (
            <div className="grid grid-cols-4 gap-x-6 gap-y-8">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="group relative flex flex-col items-center gap-1.5 w-fit mx-auto"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFile(index);
                    }}
                    className="absolute -top-2 -end-2 rounded-full bg-gray-200 p-0.5 text-gray-600 hover:bg-gray-300 hover:text-gray-900 transition-all cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <CheckCircle2 className="absolute -top-1 -start-1 h-4 w-4 text-green-500 bg-white rounded-full" />
                  <img src={wordIcon} alt="Word" className="h-12 w-12" />
                  <p className="text-xs text-center text-muted-foreground break-words max-w-[100px] leading-tight">
                    {file.name}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('importModal.noFilesYet')}
            </p>
          )}
        </div>
      </div>

      {/* Footer with Back and Next buttons */}
      <div className="flex items-center justify-between px-10 py-4 border-t bg-muted/20 shrink-0">
        <Button
          variant="outline"
          size="md"
          onClick={onBack}
          className="border-gray-300 hover:bg-gray-100 hover:text-gray-900"
        >
          {t('importModal.back')}
        </Button>
        <Button size="md" onClick={onNext} disabled={!hasFiles}>
          {t('importModal.next')}
        </Button>
      </div>
    </div>
  );
};
